import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { disableUserAccount, enableUserAccount } from '@/services/UserService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    email: string;
    mode: 'disable' | 'enable';
    onClose(): void;
}

export default function AccountAccessDialog({ email, mode, onClose }: Props) {
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isDisable = mode === 'disable';
    const title = isDisable ? 'Disable Account' : 'Enable Account';
    const confirmLabel = isDisable ? 'Disable' : 'Enable';
    const successMessage = isDisable
        ? 'User account disabled successfully.'
        : 'User account enabled successfully.';
    const failureMessage = isDisable
        ? 'Failed to disable user account. Please try again.'
        : 'Failed to enable user account. Please try again.';

    const handleConfirm = async () => {
        setIsSubmitting(true);

        try {
            if (isDisable) {
                await disableUserAccount(email);
            } else {
                await enableUserAccount(email);
            }

            await queryClient.invalidateQueries({ queryKey: ['users'] });
            successSnackbar(successMessage);
            onClose();
        } catch (error: unknown) {
            console.error(`Error ${mode}ing user account:`, error);
            const errorMessage = isAxiosError(error)
                ? (error.response?.data as string)
                : failureMessage;
            errorSnackbar(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && isSubmitting) return;
                onClose();
            }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {isDisable
                        ? 'This user will be unable to sign in until their account is re-enabled.'
                        : 'This user will be able to sign in again.'}
                </Typography>
                <Typography variant="body2">{email}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleConfirm}
                    loading={isSubmitting}
                    color={isDisable ? 'error' : 'primary'}
                    variant="contained"
                >
                    {confirmLabel}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
